import type MvArea from "models/MvArea";
import type MvSelectedGroup from "models/MvSelectedGroup";
import type MvStreetNameSelection from "models/MvStreetNameSelection";
import { getAddressCount, getZipCodeArea } from "./selectionHelpers";

export interface AddressGroup extends MvArea {
    groupedByZipCodeAndStreetName: Record<string, MvStreetNameSelection>;
}

export const getAddressGroups = (areas: MvArea[]): AddressGroup[] =>
    areas
        .filter((area) => area.result)
        .map((area) => ({
            ...area,
            groupedByZipCodeAndStreetName: area.addresses.reduce<
                Record<string, MvStreetNameSelection>
            >((groups, address) => {
                const postcode = getZipCodeArea(address);
                const streets = (groups[postcode] ??= {});
                (streets[address.straatnaam] ??= []).push(address);
                return groups;
            }, {}),
        }));

export const getInitialSelectedGroups = (
    areas: MvArea[],
    minimum?: number,
): MvSelectedGroup[] => {
    const groups = getAddressGroups(areas);
    const counts: Record<string, number> = {};
    groups.forEach((group) =>
        Object.entries(group.groupedByZipCodeAndStreetName).forEach(
            ([postcode, streets]) => {
                counts[postcode] =
                    (counts[postcode] ?? 0) +
                    Object.values(streets).reduce(
                        (count, addresses) =>
                            count + getAddressCount(addresses),
                        0,
                    );
            },
        ),
    );
    return groups.map((group) => ({
        id: group.id,
        selectedZipCodeByStreetName: Object.fromEntries(
            Object.entries(group.groupedByZipCodeAndStreetName)
                .filter(([postcode]) => !minimum || counts[postcode] >= minimum)
                .map(([postcode, streets]) => [postcode, Object.keys(streets)]),
        ),
    }));
};

export const updateZipCodeSelection = (
    groups: MvSelectedGroup[],
    areaId: string,
    postcode: string,
    update: (streets: string[]) => string[],
): MvSelectedGroup[] =>
    groups.map((group) => {
        if (group.id !== areaId) return group;
        const selectedZipCodeByStreetName = {
            ...group.selectedZipCodeByStreetName,
        };
        const streets = update(selectedZipCodeByStreetName[postcode] ?? []);
        if (streets.length) selectedZipCodeByStreetName[postcode] = streets;
        else delete selectedZipCodeByStreetName[postcode];
        return { ...group, selectedZipCodeByStreetName };
    });

export const createSelectionSummary = (
    areas: MvArea[],
    selectedGroups: MvSelectedGroup[],
    minimumPerGroup?: number,
    addressMinimum?: number,
    addressLimit?: number,
) => {
    const addressGroups = getAddressGroups(areas);
    const groupsById = new Map(addressGroups.map((group) => [group.id, group]));
    const selectedById = new Map(
        selectedGroups.map((group) => [group.id, group]),
    );
    const initialCounts: Record<string, number> = {};
    const selectedCounts: Record<string, number> = {};
    const totals: Record<
        string,
        Record<string, { initial: number; selected: number }>
    > = {};
    addressGroups.forEach((group) => {
        totals[group.id] = {};
        Object.entries(group.groupedByZipCodeAndStreetName).forEach(
            ([postcode, streets]) => {
                const selected =
                    selectedById.get(group.id)?.selectedZipCodeByStreetName[
                        postcode
                    ] ?? [];
                const initialTotal = Object.values(streets).reduce(
                    (count, addresses) => count + getAddressCount(addresses),
                    0,
                );
                const selectedTotal = selected.reduce(
                    (count, street) => count + getAddressCount(streets[street]),
                    0,
                );
                totals[group.id][postcode] = {
                    initial: initialTotal,
                    selected: selectedTotal,
                };
                initialCounts[postcode] =
                    (initialCounts[postcode] ?? 0) + initialTotal;
                selectedCounts[postcode] =
                    (selectedCounts[postcode] ?? 0) + selectedTotal;
            },
        );
    });
    const belowMinimum = (count: number) =>
        minimumPerGroup !== undefined && count > 0 && count < minimumPerGroup;

    const addressCount = Object.values(selectedCounts).reduce(
        (count, value) => count + value,
        0,
    );
    const hasSelectedGroups = selectedGroups.some(
        (group) => Object.keys(group.selectedZipCodeByStreetName).length > 0,
    );
    const belowAddressMinimum = !!(
        addressMinimum && addressCount < addressMinimum
    );
    const aboveAddressLimit = addressLimit && addressCount > addressLimit;
    return {
        belowAddressMinimum,
        aboveAddressLimit,
        addressGroups,
        addressCount,
        getZipCodeAreaTotal: (id: string, postcode: string) =>
            totals[id]?.[postcode]?.initial ?? 0,
        getZipCodeAreaSelectedTotal: (id: string, postcode: string) =>
            totals[id]?.[postcode]?.selected ?? 0,
        hasNotReachedSelectionMinimum: (postcode: string) =>
            belowMinimum(initialCounts[postcode] ?? 0),
        selectionDidNotReachSelectionMinimum: (postcode: string) =>
            belowMinimum(selectedCounts[postcode] ?? 0),
        isAllChecked: (id: string, postcode: string) => {
            const group = groupsById.get(id);
            const selected = selectedById.get(id);
            return (
                !!group &&
                !!selected &&
                Object.keys(group.groupedByZipCodeAndStreetName[postcode] ?? {})
                    .length ===
                    (selected.selectedZipCodeByStreetName[postcode] ?? [])
                        .length
            );
        },
        isConfirmDisabled: () =>
            areas.length === 0 ||
            !hasSelectedGroups ||
            belowAddressMinimum ||
            aboveAddressLimit ||
            Object.values(selectedCounts).some(belowMinimum),
    };
};

export type SelectionSummary = ReturnType<typeof createSelectionSummary>;
